import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { Fragment, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { IStoredPredicate, TPredicateMode } from '@feathr/blackbox';

import Glue from '../Glue';
import type { TPredicatesModel } from './PredicateList.utils';
import { removePredicate, updatePredicate } from './PredicateList.utils';
import Predicates from './Predicates/Predicates';
import type { TOptionsGroup } from './Predicates/utils';
import { usePredicateOptions } from './usePredicateOptions';

interface IProps {
  disabled?: boolean;
  excludeIds?: string[];
  glueClassName?: string;
  /** The mode joining the predicates, rendered as glue between rows. */
  glueMode?: TPredicateMode;
  /** The model whose `predicates` are rendered; row edits write straight back to it. */
  model: TPredicatesModel;
  optionsGroup?: TOptionsGroup;
}

function PredicateList({
  disabled = false,
  excludeIds,
  glueClassName,
  glueMode,
  model,
  optionsGroup,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const predicates = model.get('predicates') ?? [];
  const resolvedOptionsGroup = optionsGroup ?? 'segment';
  const options = usePredicateOptions(model, resolvedOptionsGroup);
  const lastItemIndex = predicates.length - 1;

  const handleEditPredicate = useCallback(
    (edited: IStoredPredicate): void => {
      updatePredicate(model, edited);
    },
    [model],
  );

  const handleRemovePredicate = useCallback(
    (key: string): void => {
      removePredicate(model, key);
    },
    [model],
  );

  return (
    <>
      {predicates.map((predicate, index) => (
        <Fragment key={predicate.key}>
          <Predicates
            disabled={disabled || model.isReadOnly}
            excludeIds={excludeIds}
            onClickRemove={handleRemovePredicate}
            onEdit={handleEditPredicate}
            options={options}
            optionsGroup={resolvedOptionsGroup}
            predicate={predicate}
          />
          <Glue
            className={glueClassName}
            isLastItem={index === lastItemIndex}
            mode={glueMode}
            t={t}
          />
        </Fragment>
      ))}
    </>
  );
}

export default observer(PredicateList);
